import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
    Plus, Search, Users, Loader2, Upload, ChevronRight, Tag,
    Mail, Phone, MapPin, Star, Filter, FileText, Zap, CheckSquare,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    active: 'bg-emerald-100 text-emerald-700',
    hired: 'bg-blue-100 text-blue-700',
    recall: 'bg-amber-100 text-amber-700',
    do_not_contact: 'bg-red-100 text-red-600',
    inactive: 'bg-stone-100 text-stone-500',
};

export default function ATSTalentPool() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [applicants, setApplicants] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [showCreate, setShowCreate] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [creating, setCreating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', email: '', phone: '' });
    const [uploadResult, setUploadResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [showEnrollDialog, setShowEnrollDialog] = useState(false);
    const [enrollCampaignId, setEnrollCampaignId] = useState('');
    const [bulkEnrolling, setBulkEnrolling] = useState(false);

    const loadApplicants = useCallback(async () => {
        try {
            const params = new URLSearchParams({ page, page_size: 50 });
            if (search) params.set('search', search);
            if (statusFilter) params.set('status', statusFilter);
            const res = await axios.get(`${API}/ats/applicants?${params}`, { headers: getAuthHeader() });
            setApplicants(res.data?.data || []);
            setTotal(res.data?.total || 0);
        } catch { toast.error('Failed to load talent pool'); }
        finally { setLoading(false); }
    }, [getAuthHeader, search, statusFilter, page]);

    useEffect(() => { loadApplicants(); }, [loadApplicants]);

    const handleCreate = async () => {
        if (!createForm.name || !createForm.email) return toast.error('Name and email are required');
        setCreating(true);
        try {
            await axios.post(`${API}/ats/applicants`, createForm, { headers: getAuthHeader() });
            toast.success('Applicant added');
            setShowCreate(false);
            setCreateForm({ name: '', email: '', phone: '' });
            loadApplicants();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add applicant'); }
        finally { setCreating(false); }
    };

    const handleCVUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUploadResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/ats/cv/upload`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            });
            setUploadResult(res.data);
            if (res.data.duplicate) {
                toast.info('Duplicate CV detected — showing existing parse');
            } else {
                toast.success(`CV parsed (${Math.round((res.data.confidence || 0) * 100)}% confidence)`);
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'CV upload failed'); }
        finally { setUploading(false); }
    };

    const handleAcceptCV = async () => {
        if (!uploadResult?.cv_id) return;
        try {
            const res = await axios.post(`${API}/ats/cv/${uploadResult.cv_id}/accept`, {}, { headers: getAuthHeader() });
            toast.success(res.data.created ? 'Applicant created from CV' : 'Applicant profile updated from CV');
            setShowUpload(false);
            setUploadResult(null);
            loadApplicants();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept CV'); }
    };

    // Load activation campaigns for bulk enroll
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/ats/activation-campaigns?page_size=100`, { headers: getAuthHeader() });
                setCampaigns(res.data?.data || []);
            } catch { /* ignore */ }
        })();
    }, [getAuthHeader]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleSelectAll = () => {
        if (selectedIds.length === applicants.length) setSelectedIds([]);
        else setSelectedIds(applicants.map(a => a.applicant_id));
    };

    const handleBulkEnroll = async () => {
        if (!enrollCampaignId) return toast.error('Select a campaign');
        setBulkEnrolling(true);
        try {
            const res = await axios.post(`${API}/ats/activation-campaigns/${enrollCampaignId}/bulk-enroll`, {
                applicant_ids: selectedIds,
            }, { headers: getAuthHeader() });
            toast.success(`Enrolled ${res.data.enrolled} applicant(s)`);
            setShowEnrollDialog(false);
            setSelectedIds([]);
            setEnrollCampaignId('');
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrollment failed'); }
        finally { setBulkEnrolling(false); }
    };

    const pageCount = Math.ceil(total / 50);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Talent Pool
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">{total} applicant{total !== 1 ? 's' : ''} in your talent database</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => setShowUpload(true)} variant="outline">
                        <Upload className="w-4 h-4 mr-2" /> Upload CV
                    </Button>
                    <Button onClick={() => setShowCreate(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-2" /> Add Applicant
                    </Button>
                </div>
            </div>

            {/* Search + Filters */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                        placeholder="Search by name, email, or headline..."
                        className="w-full pl-10 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                </div>
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                    className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="hired">Hired</option>
                    <option value="recall">Recall</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && (
                <div className="flex items-center gap-3 bg-stone-900 text-white rounded-xl px-4 py-2.5 animate-fade-in">
                    <CheckSquare className="w-4 h-4" />
                    <span className="text-sm font-medium">{selectedIds.length} selected</span>
                    <div className="h-4 w-px bg-stone-600" />
                    <Button size="sm" variant="ghost" className="text-white hover:bg-stone-700"
                        onClick={() => setShowEnrollDialog(true)}>
                        <Zap className="w-4 h-4 mr-1" /> Enroll in Campaign
                    </Button>
                    <button onClick={() => setSelectedIds([])} className="ml-auto text-xs text-stone-400 hover:text-white">Clear</button>
                </div>
            )}

            {/* Applicant List */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : applicants.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-stone-200">
                    <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">No applicants yet</p>
                    <p className="text-xs text-stone-400 mt-1">Upload CVs or add applicants manually to build your talent pool</p>
                    <div className="flex justify-center gap-2 mt-4">
                        <Button onClick={() => setShowUpload(true)} variant="outline" size="sm">
                            <Upload className="w-4 h-4 mr-1" /> Upload CV
                        </Button>
                        <Button onClick={() => setShowCreate(true)} variant="outline" size="sm">
                            <Plus className="w-4 h-4 mr-1" /> Add Manually
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {/* Select All */}
                    <div className="flex items-center gap-2 px-2 py-1">
                        <input type="checkbox" checked={selectedIds.length === applicants.length && applicants.length > 0}
                            onChange={toggleSelectAll} className="rounded border-stone-300 text-stone-900 focus:ring-stone-900" />
                        <span className="text-xs text-stone-500">Select all on page</span>
                    </div>
                    {applicants.map(a => (
                        <div key={a.applicant_id}
                            className={`bg-white rounded-xl border p-4 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer ${
                                selectedIds.includes(a.applicant_id) ? 'border-stone-400 bg-stone-50' : 'border-stone-200'
                            }`}>
                            <div className="flex items-center gap-4">
                                {/* Checkbox */}
                                <input type="checkbox" checked={selectedIds.includes(a.applicant_id)}
                                    onChange={e => { e.stopPropagation(); toggleSelect(a.applicant_id); }}
                                    onClick={e => e.stopPropagation()}
                                    className="rounded border-stone-300 text-stone-900 focus:ring-stone-900 flex-shrink-0" />
                                {/* Avatar */}
                                <div onClick={() => navigate(`/recruitment/talent-pool/${a.applicant_id}`)}
                                    className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 font-semibold text-sm flex-shrink-0">
                                    {(a.name || '?')[0].toUpperCase()}
                                </div>
                                {/* Info */}
                                <div className="flex-1 min-w-0" onClick={() => navigate(`/recruitment/talent-pool/${a.applicant_id}`)}>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-stone-900 text-sm">{a.name}</span>
                                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[a.status] || 'bg-stone-100 text-stone-500'}`}>
                                            {a.status}
                                        </span>
                                        {a.profile_completeness > 0 && (
                                            <span className="text-[10px] text-stone-400">{a.profile_completeness}% complete</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-500">
                                        {a.profile?.headline && <span className="truncate max-w-[200px]">{a.profile.headline}</span>}
                                        <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{a.email}</span>
                                        {a.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{a.phone}</span>}
                                        {a.profile?.location?.city && (
                                            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{a.profile.location.city}</span>
                                        )}
                                    </div>
                                    {a.tags?.length > 0 && (
                                        <div className="flex gap-1 mt-1.5">
                                            {a.tags.slice(0, 5).map(tag => (
                                                <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-stone-50 border border-stone-200 rounded text-[10px] text-stone-500">
                                                    <Tag className="w-2.5 h-2.5" />{tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Skills preview */}
                                <div className="hidden md:flex flex-wrap gap-1 max-w-[200px]">
                                    {(a.profile?.skills || []).slice(0, 4).map((s, i) => (
                                        <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded">
                                            {s.name || s}
                                        </span>
                                    ))}
                                </div>
                                <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                            </div>
                        </div>
                    ))}

                    {/* Pagination */}
                    {pageCount > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-4">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                            <span className="text-sm text-stone-500">Page {page} of {pageCount}</span>
                            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Next</Button>
                        </div>
                    )}
                </div>
            )}

            {/* Add Applicant Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Add Applicant</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Full Name *</label>
                            <input value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Email *</label>
                            <input type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Phone</label>
                            <input value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating} className="bg-stone-900 text-white hover:bg-stone-800">
                            {creating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Adding...</> : 'Add Applicant'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upload CV Dialog */}
            <Dialog open={showUpload} onOpenChange={v => { setShowUpload(v); if (!v) setUploadResult(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Upload CV</DialogTitle></DialogHeader>
                    <div className="py-2">
                        {!uploadResult ? (
                            <div className="border-2 border-dashed border-stone-200 rounded-xl p-8 text-center">
                                <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                                <p className="text-sm text-stone-600 mb-1">Upload a CV to auto-parse applicant profile</p>
                                <p className="text-xs text-stone-400 mb-4">Supports PDF, DOCX, DOC (max 10MB)</p>
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-lg text-sm cursor-pointer hover:bg-stone-800">
                                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing...</> : <><Upload className="w-4 h-4" /> Choose File</>}
                                    <input type="file" accept=".pdf,.docx,.doc" className="hidden" onChange={handleCVUpload} disabled={uploading} />
                                </label>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                    <p className="text-sm font-medium text-emerald-800">
                                        CV parsed ({Math.round((uploadResult.confidence || 0) * 100)}% confidence)
                                    </p>
                                    {uploadResult.injection_flags?.length > 0 && (
                                        <p className="text-xs text-red-600 mt-1">
                                            Warning: {uploadResult.injection_flags.length} potential injection flag(s) detected
                                        </p>
                                    )}
                                </div>
                                {uploadResult.parsed_profile && (
                                    <div className="bg-stone-50 rounded-lg p-4 text-sm space-y-2 max-h-60 overflow-y-auto">
                                        <p><strong>Name:</strong> {uploadResult.parsed_profile.name || '—'}</p>
                                        <p><strong>Email:</strong> {uploadResult.parsed_profile.email || '—'}</p>
                                        <p><strong>Headline:</strong> {uploadResult.parsed_profile.headline || '—'}</p>
                                        <p><strong>Skills:</strong> {(uploadResult.parsed_profile.skills || []).map(s => s.name || s).join(', ') || '—'}</p>
                                        <p><strong>Experience:</strong> {(uploadResult.parsed_profile.work_history || []).length} positions</p>
                                        <p><strong>Education:</strong> {(uploadResult.parsed_profile.education || []).length} entries</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {uploadResult && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setUploadResult(null)}>Upload Another</Button>
                            <Button onClick={handleAcceptCV} className="bg-stone-900 text-white hover:bg-stone-800">
                                Accept & Create Applicant
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>

            {/* Enroll in Campaign Dialog */}
            <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Enroll in Activation Campaign</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <p className="text-sm text-stone-600">
                            Enroll <strong>{selectedIds.length}</strong> applicant{selectedIds.length !== 1 ? 's' : ''} in a campaign
                        </p>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Campaign</label>
                            <select value={enrollCampaignId} onChange={e => setEnrollCampaignId(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white">
                                <option value="">Select a campaign...</option>
                                {campaigns.filter(c => c.status === 'active' || c.status === 'draft').map(c => (
                                    <option key={c.campaign_id} value={c.campaign_id}>{c.name} ({c.status})</option>
                                ))}
                            </select>
                        </div>
                        {campaigns.length === 0 && (
                            <p className="text-xs text-stone-400">No campaigns yet. Create one from the Activation page first.</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEnrollDialog(false)}>Cancel</Button>
                        <Button onClick={handleBulkEnroll} disabled={bulkEnrolling || !enrollCampaignId}
                            className="bg-stone-900 text-white hover:bg-stone-800">
                            {bulkEnrolling ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Enrolling...</> : <><Zap className="w-4 h-4 mr-1" /> Enroll</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
